/**
 * ResultScreenGlobal.tsx - Global results comparison for the Loss-Gain Framing experiment (Unified Design)
 * Last updated: 2025-11-17 13:35 UTC
 * Updated by: JulianPolly & Copilot
 */

import React, { useEffect, useState, useMemo } from "react";
import { Bar, BarChart, Tooltip, XAxis, YAxis, ResponsiveContainer, LabelList, Legend } from "recharts";
import { useSession } from "../../../SessionContext";
import { ScreenConfig } from "../Sequences";
import { LGFramingKey } from "./LGFramingSequence";
import { useLanguage } from "../../../../context/LanguageContext";
import { colors, commonStyles } from "../../../../components/colors";

const gainColor = "#238551";
const lossColor = colors.sienna;

const cardStyle: React.CSSProperties = {
    ...commonStyles.card,
    margin: 0,
    minWidth: 880,
    maxWidth: 880,
    width: "100%",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    minHeight: 420,
    boxSizing: "border-box"
};

export const ResultScreenGlobalComponent = () => {
    const { getGroupData, userData } = useSession();
    const { t, language } = useLanguage();
    const [dataOwnGroup, setDataOwnGroup] = useState<any[]>([]);
    const [globalLG, setGlobalLG] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingGlobal, setLoadingGlobal] = useState(true);

    // Übersetzungen laden
    const loadingText = t('experiments', 'LGFraming', 'screens', 'results', 'loading');
    const yourClassTitle = t('experiments', 'LGFraming', 'screens', 'resultsGlobal', 'yourClass');
    const allClassesTitle = t('experiments', 'LGFraming', 'screens', 'resultsGlobal', 'allClasses');
    const gainFrameLabel = t('experiments', 'LGFraming', 'screens', 'resultsGlobal', 'gainFrame');
    const lossFrameLabel = t('experiments', 'LGFraming', 'screens', 'resultsGlobal', 'lossFrame');

    // Slider-Labels
    const sliderLabels = useMemo(() => {
        return language === 'en'
            ? [
                "very unlikely",
                "somewhat unlikely",
                "undecided",
                "somewhat likely",
                "very likely"
            ]
            : [
                "sehr unwahrscheinlich",
                "eher unwahrscheinlich",
                "unentschieden",
                "eher wahrscheinlich",
                "sehr wahrscheinlich"
            ];
    }, [language]);

    // Custom Tooltip with colored value and percentage
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            const total = payload.reduce((sum: number, p: any) => sum + (p.value || 0), 0);
            return (
                <div style={{
                    background: "#fff",
                    border: `1px solid ${colors.silver}`,
                    padding: 12,
                    borderRadius: 8,
                    fontSize: 14,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
                }}>
                    <div style={{
                        fontWeight: 600,
                        marginBottom: 6,
                        color: colors.brillantBlue
                    }}>
                        {label}
                    </div>
                    {payload.map((p: any) => {
                        const perc = total > 0 ? Math.round((p.value / total) * 100) : 0;
                        return (
                            <div key={p.dataKey} style={{ color: p.color || colors.textMedium }}>
                                {p.name}: {p.value} ({perc}%)
                            </div>
                        );
                    })}
                </div>
            );
        }
        return null;
    };

    // Eigene Gruppe bestimmen (with robust normalization)
    const eigeneGruppe = userData?.gruppe ?? "";

    // Helper to normalize group names (trimming and lowercasing)
    const normalizeGroup = (name: string | undefined | null) =>
        (name ?? "").trim().toLowerCase();

    useEffect(() => {
        setLoading(true);
        getGroupData().then(allData => {
            const initArr = () => Array(5).fill(0).map(() => ({ gain: 0, loss: 0 }));
            const own = initArr();
            const userGroupNorm = normalizeGroup(eigeneGruppe);

            allData?.forEach((dat: any) => {
                const gruppe = dat.gruppe;
                const gruppeNorm = normalizeGroup(gruppe);

                if (gruppeNorm !== userGroupNorm) return; // Only own group

                if (dat[LGFramingKey]) {
                    Object.values(dat[LGFramingKey]).forEach((result: any) => {
                        if (result?.selections?.length > 0) {
                            const selection = result.selections[result.selections.length - 1];
                            if (
                                typeof selection.sliderValue === "number" &&
                                selection.sliderValue >= 0 &&
                                selection.sliderValue <= 4 &&
                                typeof selection.bedingung === "string"
                            ) {
                                if (selection.bedingung === "Bedingung 1") {
                                    own[selection.sliderValue].gain++;
                                }
                                if (selection.bedingung === "Bedingung 2") {
                                    own[selection.sliderValue].loss++;
                                }
                            }
                        }
                    });
                }
            });
            const rechartsOwn = sliderLabels.map((label, idx) => ({
                name: label,
                [gainFrameLabel]: own[idx].gain,
                [lossFrameLabel]: own[idx].loss
            }));
            setDataOwnGroup(rechartsOwn);
            setLoading(false);
        }).catch(error => {
            setLoading(false);
            console.error("Error fetching group data:", error);
        });
    }, [getGroupData, eigeneGruppe, sliderLabels, gainFrameLabel, lossFrameLabel]);

    // New: Load global aggregate LGFraming data
    useEffect(() => {
        setLoadingGlobal(true);
        fetch("/api/aggregateGlobalData")
            .then(res => res.json())
            .then((apiJson) => {
                const LG = apiJson?.data?.LGFraming;
                if (!LG) {
                    setGlobalLG([]);
                    setLoadingGlobal(false);
                    return;
                }
                // Transform backend structure to recharts format
                const arr = sliderLabels.map((label: string, idx: number) => ({
                    name: label,
                    [gainFrameLabel]: LG["Bedingung 1"]?.[idx] ?? 0,
                    [lossFrameLabel]: LG["Bedingung 2"]?.[idx] ?? 0,
                }));
                setGlobalLG(arr);
                setLoadingGlobal(false);
            }).catch(() => {
            setGlobalLG([]);
            setLoadingGlobal(false);
        });
    }, [sliderLabels, gainFrameLabel, lossFrameLabel]);

    if (loading) {
        return <div style={{
            textAlign: "center",
            marginTop: 50,
            fontSize: 17,
            color: colors.brillantBlue
        }}>{loadingText}</div>;
    }

    const thinBarSize = 20;

    return (
        <div className="experiment-container" style={commonStyles.container}>
            <h2 style={{
                ...commonStyles.heading,
                margin: "0 0 24px 0",
                fontSize: 28,
                fontWeight: 700
            }}>
                {t('experiments', 'LGFraming', 'screens', 'resultsGlobal', 'title')}
            </h2>
            <div style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 48,
                marginTop: 24,
                width: "100%"
            }}>
                <div style={cardStyle}>
                    <h3 style={{
                        ...commonStyles.heading,
                        fontSize: 20,
                        marginBottom: 10
                    }}>
                        {yourClassTitle}
                    </h3>
                    <div style={{ flex: 1, width: "100%" }}>
                        <ResponsiveContainer width="100%" height={360}>
                            <BarChart
                                data={dataOwnGroup}
                                margin={{ top: 30, right: 20, left: 10, bottom: 60 }}
                            >
                                <XAxis
                                    dataKey="name"
                                    interval={0}
                                    tick={{ fontSize: 13, angle: -30, textAnchor: "end" } as any}
                                    height={90}
                                />
                                <YAxis allowDecimals={false} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                <Bar dataKey={gainFrameLabel} fill={gainColor} name={gainFrameLabel} barSize={thinBarSize}>
                                    <LabelList dataKey={gainFrameLabel} position="top" fontSize={13} />
                                </Bar>
                                <Bar dataKey={lossFrameLabel} fill={lossColor} name={lossFrameLabel} barSize={thinBarSize}>
                                    <LabelList dataKey={lossFrameLabel} position="top" fontSize={13} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div style={cardStyle}>
                    <h3 style={{
                        ...commonStyles.heading,
                        fontSize: 20,
                        marginBottom: 10
                    }}>
                        {allClassesTitle ?? "Alle Klassen (gesamt)"}
                    </h3>
                    <div style={{ flex: 1, width: "100%" }}>
                        {loadingGlobal ? (
                            <div style={{ color: colors.brillantBlue, fontSize: 17, textAlign: "center", marginTop: 40 }}>
                                {loadingText}
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={360}>
                                <BarChart
                                    data={globalLG}
                                    margin={{ top: 30, right: 20, left: 10, bottom: 60 }}
                                >
                                    <XAxis
                                        dataKey="name"
                                        interval={0}
                                        tick={{ fontSize: 13, angle: -30, textAnchor: "end" } as any}
                                        height={90}
                                    />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                    <Bar dataKey={gainFrameLabel} fill={gainColor} name={gainFrameLabel} barSize={thinBarSize}>
                                        <LabelList dataKey={gainFrameLabel} position="top" fontSize={13} />
                                    </Bar>
                                    <Bar dataKey={lossFrameLabel} fill={lossColor} name={lossFrameLabel} barSize={thinBarSize}>
                                        <LabelList dataKey={lossFrameLabel} position="top" fontSize={13} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const ResultScreenGlobal: ScreenConfig = {
    component: ResultScreenGlobalComponent,
    defaultResult: {
        selections: [],
    },
    canContinueDefault: true,
};